
import { useState } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Trash2, Shield, AlertTriangle, Lock } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { exportDeleteService } from '@/services/exportDeleteService';
import { useRoleAccess } from '@/hooks/useRoleAccess';

const ExportDelete = () => {
  const { canExportReports, canDeleteReports, isViewer } = useRoleAccess();
  
  const [exportForm, setExportForm] = useState({
    user_id: '',
    project_id: '',
    format: 'json',
    date_from: '',
    date_to: ''
  });

  const [deleteForm, setDeleteForm] = useState({
    user_id: '',
    project_id: '',
    confirmation: ''
  });

  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleExport = async () => {
    if (!canExportReports) {
      toast.error('Access denied', {
        description: 'Only team admins can export memory data'
      });
      return;
    }
    
    if (!exportForm.user_id && !exportForm.project_id) {
      toast.error('Please specify at least user_id or project_id');
      return;
    }

    setIsExporting(true);
    try {
      await exportDeleteService.exportMemories({
        user_id: exportForm.user_id,
        project_id: exportForm.project_id,
        format: exportForm.format,
        date_from: exportForm.date_from,
        date_to: exportForm.date_to
      });

      toast.success('Export completed', {
        description: `${exportForm.format.toUpperCase()} export downloaded successfully`
      });
    } catch (error) {
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Failed to export memories'
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDelete = async () => {
    if (!canDeleteReports) {
      toast.error('Access denied', {
        description: 'Only team admins can delete memory data'
      });
      return;
    }
    
    if (!deleteForm.user_id && !deleteForm.project_id) {
      toast.error('Please specify user_id or project_id to delete');
      return;
    }

    if (deleteForm.confirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      const result = await exportDeleteService.bulkDeleteMemories({
        user_id: deleteForm.user_id,
        project_id: deleteForm.project_id,
        confirmation: deleteForm.confirmation
      });

      toast.success('Right-to-be-forgotten executed', {
        description: `Successfully deleted ${result.deletedCount} memory entries for ${deleteForm.user_id || deleteForm.project_id}`
      });

      // Reset form
      setDeleteForm({ user_id: '', project_id: '', confirmation: '' });
    } catch (error) {
      toast.error('Deletion failed', {
        description: error instanceof Error ? error.message : 'Failed to delete memories'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isViewer) {
    return (
      <PageLayout 
        title="Export & Delete" 
        description="Compliance tools for data export and right-to-be-forgotten requests"
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Lock className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Access Restricted</h3>
            <p className="text-muted-foreground mb-4">
              Export and delete operations are restricted to team administrators.
            </p>
            <p className="text-sm text-muted-foreground">
              Contact your team admin if you need to export or delete memory data.
            </p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="Export & Delete" 
      description="Compliance tools for data export and right-to-be-forgotten requests"
    >
      <Tabs defaultValue="export" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="export" className="flex items-center gap-2">
            <Download size={16} />
            Export Data
          </TabsTrigger>
          <TabsTrigger value="delete" className="flex items-center gap-2">
            <Trash2 size={16} />
            Delete Data
          </TabsTrigger>
        </TabsList>

        <TabsContent value="export">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download size={20} />
                Export Memory Data
              </CardTitle>
              <CardDescription>
                Generate compliance exports of memory data for audit purposes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="export_user_id">User ID</Label>
                  <Input
                    id="export_user_id"
                    placeholder="user_123"
                    value={exportForm.user_id}
                    onChange={(e) => setExportForm({...exportForm, user_id: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="export_project_id">Project ID</Label>
                  <Input
                    id="export_project_id"
                    placeholder="project_abc"
                    value={exportForm.project_id}
                    onChange={(e) => setExportForm({...exportForm, project_id: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_from">From Date</Label>
                  <Input
                    id="date_from"
                    type="date"
                    value={exportForm.date_from}
                    onChange={(e) => setExportForm({...exportForm, date_from: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date_to">To Date</Label>
                  <Input
                    id="date_to"
                    type="date"
                    value={exportForm.date_to}
                    onChange={(e) => setExportForm({...exportForm, date_to: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Export Format</Label>
                <Select value={exportForm.format} onValueChange={(value) => setExportForm({...exportForm, format: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="json">JSON</SelectItem>
                    <SelectItem value="csv">CSV</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={handleExport} className="w-full" disabled={isExporting}>
                <Download size={16} className="mr-2" />
                {isExporting ? 'Generating...' : 'Generate Export'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="delete">
          <Card className="border-destructive/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <Shield size={20} />
                Right-to-be-Forgotten
              </CardTitle>
              <CardDescription>
                Permanently delete all memory entries for a user or team. This action cannot be undone.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="delete_user_id">User ID</Label>
                  <Input
                    id="delete_user_id"
                    placeholder="user_123"
                    value={deleteForm.user_id}
                    onChange={(e) => setDeleteForm({...deleteForm, user_id: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="delete_project_id">Project ID</Label>
                  <Input
                    id="delete_project_id"
                    placeholder="project_abc"
                    value={deleteForm.project_id}
                    onChange={(e) => setDeleteForm({...deleteForm, project_id: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmation">Type "DELETE" to confirm</Label>
                <Input
                  id="confirmation"
                  placeholder="DELETE"
                  value={deleteForm.confirmation}
                  onChange={(e) => setDeleteForm({...deleteForm, confirmation: e.target.value})}
                />
              </div>

              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
                <div className="flex items-center gap-2 text-destructive mb-2">
                  <AlertTriangle size={16} />
                  <span className="font-semibold">Warning</span>
                </div>
                <p className="text-sm text-destructive">
                  This will permanently delete all memory entries associated with the specified user or team. 
                  This action cannot be reversed and is performed to comply with right-to-be-forgotten requests.
                </p>
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="destructive" 
                    className="w-full"
                    disabled={deleteForm.confirmation !== 'DELETE' || isDeleting}
                  >
                    <Trash2 size={16} className="mr-2" />
                    {isDeleting ? 'Deleting...' : 'Execute Deletion'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you absolutely sure? This will permanently delete all memory entries for{' '}
                      {deleteForm.user_id || deleteForm.project_id}. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleDelete} 
                      className="bg-destructive hover:bg-destructive/90"
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete Permanently'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </PageLayout>
  );
};

export default ExportDelete;
